// Designer.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { DesignerLayout } from "../layout/DesignerLayout";
import Sections from "../components/Sections";
import RightPanelToggle from "../components/RightPanelToggle";
import Main from "../ThreeJs/Main";
import LoadingScreen from "../components/LoadingScreen";
import { useParams } from "react-router-dom";
import {
  checkUser,
  getDesign,
  getPanelImages,
  getPanelList,
  updateProjectData,
} from "../helpers/API";
import NotFound from "../components/errorsscreens/NotFound";
import { DEV_MOD, handleErrors } from "../helpers/CONSTANT";
import { notifyApiError } from "../helpers/errorNotify";
import { setPanelImage } from "../helpers/panelImageCache";
import { DrawingModes, POLYGONS, TREES, VERTICES, LABELS, VARS } from "../ThreeJs/Global/variables";
import DeleteConfirmation from "../components/DeleteConfirmation";
import { deleteRuler, RULER_DELETE_REQUESTED_EVENT } from "../ThreeJs/Ruler/RulerStore";
import { toast } from "../lib/toast";
import { transformPolygonData } from "../ThreeJs/DataUtils/SavaData";
import { generateDxf } from "../ThreeJs/Files/Dxf/DxfGenerator";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  deleteObstacleIndex,
  deletePanelIndex,
  deleteSegmentIndex,
  selectedIndicesState,
  polygonsState,
  panelSettings,
  panelsOK,
} from "../ThreeJs/ReactStates/states";
import {
  deleteObstacle,
  reindexObstaclesAfterSegmentDelete,
  reindexObstaclesAfterSegmentInsert,
} from "../ThreeJs/Obstacles/ObstacleFunctions";
import {
  deleteObstacleSelector,
  deletePanelsSelector,
  deleteSegmentSelector,
  updatePolygonDetailsSelector,
} from "../ThreeJs/ReactStates/updates";
import {
  removeSegmentDragControls,
  setSegmentDragControls,
} from "../ThreeJs/Segments/DragControls";
import {
  removeExtraPanels,
  removeGrid,
  checkPanelsOK,
  syncPanelSelectionVisuals,
} from "../ThreeJs/Panels/PanelFunctions";
import ObstacleCopyPasteHotkeys from "../components/ObstacleCopyPasteHotkeys";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import * as THREE from "three";
import { selectSegment } from "../ThreeJs/Segments/Functions";
import { updateSegmentLabel, indexToLabel } from "../ThreeJs/Segments/Labels";
import { isIrradianceOverlay } from "../ThreeJs/Shading/ASADisplay";
import { uploadDesignerReport } from "../helpers/reportUpload";
import { persistReportBranding } from "../helpers/reportBranding";

// 🔄 Undo manager helpers
import {
  canUndo,
  UndoLastAction,
  clearUndoStack,
  disableUndoRecording,
  enableUndoRecording,
  pushUndoAction,
} from "../ThreeJs/Global/UndoManager";

const DRAWING_CURSOR_CLASS = "drawing-plus-cursor";
const DRAWING_CURSOR_MODES = new Set([
  DrawingModes.SEGMENT,
  DrawingModes.OBSTACLE,
  DrawingModes.PANEL,
  DrawingModes.TRIM,
  DrawingModes.RULER,
]);

const Designer = () => {
  const [currentDrawingMode, setCurrentDrawingMode] = useState(DrawingModes.NONE);
  const currentDrawingModeRef = useRef(DrawingModes.NONE);
  const [obstacleType, setObstacleType] = useState(null);
  const obstacleTypeRef = useRef(null);

  const [showShadeLoader, setShowShadeLoader] = useState(false);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  // Ruler measurements live outside the recoil delete-index atoms, so they carry
  // their own confirmation target.
  const [rulerDeleteTarget, setRulerDeleteTarget] = useState(null);

  const [deleteObsIndex, setDeleteObsIndex] = useRecoilState(deleteObstacleIndex);
  const [deleteSegIndex, setDeleteSegIndex] = useRecoilState(deleteSegmentIndex);
  const [deletePanIndex, setDeletePanIndex] = useRecoilState(deletePanelIndex);
  const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState);

  const setShowPanelSettings = useSetRecoilState(panelSettings);
  const polygons = useRecoilValue(polygonsState);
  const setPolygons = useSetRecoilState(polygonsState);
  const updateObstacleList = useSetRecoilState(deleteObstacleSelector);
  const updateSegmentList = useSetRecoilState(deleteSegmentSelector);
  const updatePanelList = useSetRecoilState(deletePanelsSelector);
  const setPolygonDetails = useSetRecoilState(updatePolygonDetailsSelector);
  const [arePanelsOK, setPanelsOK] = useRecoilState(panelsOK);

  const [hasUndo, setHasUndo] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [informationChecked, setInformationChecked] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [modelProgress, setModelProgress] = useState(0);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [panelList, setPanelList] = useState(null);
  const { token, designID } = useParams();

  //  NEW: selected tree state (from enableTreeDrawing.js clicks)
  const [selectedTree, setSelectedTree] = useState(null);
  const [selectedTrimSphere, setSelectedTrimSphere] = useState(null);
  const [activeRightPanel, setActiveRightPanel] = useState("segments");
  const activeRightPanelRef = useRef("segments");
  const prevSelectedIndicesRef = useRef({
    segmentIndex: null,
    obstacleIndex: null,
    panelIndex: null,
  });

  const refreshUndoState = () => setHasUndo(canUndo());

  const updateDrawingMode = (newMode) => {
    setCurrentDrawingMode(newMode);
    currentDrawingModeRef.current = newMode;
    if (typeof document !== "undefined") {
      document.body.classList.toggle(
        DRAWING_CURSOR_CLASS,
        DRAWING_CURSOR_MODES.has(newMode)
      );
      if (newMode !== DrawingModes.NONE) {
        document.body.classList.remove("tree-drawing-plus-cursor");
      }
    }
  };

  const updateObstacleType = (type) => {
    setObstacleType(type);
    obstacleTypeRef.current = type;
  };

  const handleDeleteCancellation = () => {
    setDeleteConfirmationVisible(false);
    setDeleteSegIndex(null);
    setDeleteObsIndex(null);
    setDeletePanIndex(null);
  };

  // The ruler label's delete button and the Delete key both raise this event
  // rather than removing the measurement outright.
  useEffect(() => {
    const handleRulerDeleteRequest = (event) => {
      setRulerDeleteTarget(event.detail?.ruler ?? null);
    };

    window.addEventListener(RULER_DELETE_REQUESTED_EVENT, handleRulerDeleteRequest);
    return () => {
      window.removeEventListener(RULER_DELETE_REQUESTED_EVENT, handleRulerDeleteRequest);
    };
  }, []);

  const handleRulerDeleteConfirmation = () => {
    if (rulerDeleteTarget) deleteRuler(rulerDeleteTarget, { recordUndo: true });
    setRulerDeleteTarget(null);
  };

  const buildSegmentDeleteSnapshot = (index) => {
    const segment = POLYGONS[index];
    if (!segment) return null;

    const uiSegmentRaw = polygons?.[index] ?? null;
    const uiSegment = uiSegmentRaw
      ? {
          ...uiSegmentRaw,
          obstructions: Array.isArray(uiSegmentRaw.obstructions)
            ? uiSegmentRaw.obstructions.map((o) => ({ ...o }))
            : [],
          panels: Array.isArray(uiSegmentRaw.panels)
            ? uiSegmentRaw.panels.map((p) => ({ ...p }))
            : [],
        }
      : null;

    const measurementLabels = [];
    if (Array.isArray(segment.edges)) {
      segment.edges.forEach((edge) => {
        if (!edge?.id) return;
        const label = LABELS.measurements[edge.id];
        if (label) measurementLabels.push({ id: edge.id, label });
      });
    }

    const irradianceSprites = [];
    const scene = sceneManager.scene;
    const segmentId = segment.id ?? null;
    if (scene) {
      scene.traverse((obj) => {
        if (!isIrradianceOverlay(obj)) return;
        const matchesId = segmentId != null && obj.userData?.segmentId === segmentId;
        const matchesIndex = Number.isInteger(index) && obj.userData?.segmentIndex === index;
        if (matchesId || matchesIndex) irradianceSprites.push(obj);
      });
    }

    return {
      index,
      segment,
      vertices: VERTICES[index],
      uiSegment,
      measurementLabels,
      segmentLabel: segment.label || null,
      setbackMesh: segment.setBacks?.mesh || null,
      irradianceSprites,
      wasSelected: selectedIndices.segmentIndex === index,
    };
  };

  const removeSegmentBySnapshot = (snapshot) => {
    if (!snapshot) return;
    const { index, segment, measurementLabels, setbackMesh, irradianceSprites } = snapshot;
    const scene = sceneManager.scene;

    if (segment?.shape) {
      scene.remove(segment.shape);
    }
    if (segment?.label) {
      scene.remove(segment.label);
    }

    measurementLabels.forEach(({ id, label }) => {
      if (label) scene.remove(label);
      if (id && LABELS.measurements[id]) {
        delete LABELS.measurements[id];
      }
    });

    if (setbackMesh) {
      scene.remove(setbackMesh);
    }

    if (Array.isArray(segment?.obstructions)) {
      segment.obstructions.forEach((obs) => {
        if (obs?.mesh) scene.remove(obs.mesh);
      });
    }

    if (Array.isArray(segment?.panels)) {
      segment.panels.forEach((panelData) => {
        if (panelData?.panel) scene.remove(panelData.panel);
      });
    }

    irradianceSprites.forEach((sprite) => {
      if (sprite?.parent) sprite.parent.remove(sprite);
      else scene.remove(sprite);
    });

    POLYGONS.splice(index, 1);
    VERTICES.splice(index, 1);
    updateSegmentLabel();
    reindexObstaclesAfterSegmentDelete(index);
  };

  const restoreSegmentFromSnapshot = (snapshot) => {
    if (!snapshot) return;
    const {
      index,
      segment,
      vertices,
      uiSegment,
      measurementLabels,
      segmentLabel,
      setbackMesh,
      irradianceSprites,
      wasSelected,
    } = snapshot;

    const scene = sceneManager.scene;

    reindexObstaclesAfterSegmentInsert(index);
    POLYGONS.splice(index, 0, segment);
    VERTICES.splice(index, 0, vertices);

    if (segment?.shape) scene.add(segment.shape);
    if (segmentLabel) scene.add(segmentLabel);

    measurementLabels.forEach(({ id, label }) => {
      if (!label) return;
      scene.add(label);
      if (id) LABELS.measurements[id] = label;
    });

    if (setbackMesh) scene.add(setbackMesh);

    if (Array.isArray(segment?.obstructions)) {
      segment.obstructions.forEach((obs) => {
        if (obs?.mesh) scene.add(obs.mesh);
      });
    }

    if (Array.isArray(segment?.panels)) {
      segment.panels.forEach((panelData) => {
        if (panelData?.panel) scene.add(panelData.panel);
      });
    }

    irradianceSprites.forEach((sprite) => {
      if (sprite) scene.add(sprite);
    });

    updateSegmentLabel();

    if (uiSegment && typeof setPolygons === "function") {
      setPolygons((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        next.splice(index, 0, uiSegment);
        return next.map((seg, i) => ({
          ...seg,
          label: indexToLabel(i),
        }));
      });
    }

    if (wasSelected) {
      selectSegment(
        { segmentIndex: null, obstacleIndex: null, panelIndex: null },
        index,
        setSelectedIndices,
        setPolygonDetails
      );
    }
  };

  const handleDeleteConfirmation = () => {
    let segmentIndex = selectedIndices.segmentIndex;
    let obsIndex = selectedIndices.obstacleIndex;
    let panIndex = selectedIndices.panelIndex;

    if (deleteObsIndex != null) {
      deleteObstacle(segmentIndex, deleteObsIndex, { setPolygons });
      updateObstacleList(deleteObsIndex);
      toast.success("Obstacle deleted");

      if (deleteObsIndex === obsIndex) {
        updateSelection(segmentIndex, null, panIndex);
        if (segmentIndex !== null && segmentIndex !== undefined) {
          setSegmentDragControls(segmentIndex, setPolygonDetails);
        }
      }
      if (deleteObsIndex < obsIndex) {
        updateSelection(segmentIndex, obsIndex - 1, panIndex);
      }
      setDeleteObsIndex(null);
    } else if (deleteSegIndex != null) {
      const snapshot = buildSegmentDeleteSnapshot(deleteSegIndex);
      if (snapshot) {
        removeSegmentDragControls();
        removeSegmentBySnapshot(snapshot);
        updateSegmentList(deleteSegIndex);
        toast.success("Section deleted");

        pushUndoAction({
          label: "Delete segment",
          undo: () => {
            restoreSegmentFromSnapshot(snapshot);
          },
          redo: () => {
            removeSegmentDragControls();
            removeSegmentBySnapshot(snapshot);
            if (typeof setPolygons === "function") {
              setPolygons((prev) => {
                if (!prev) return prev;
                const next = [...prev];
                next.splice(snapshot.index, 1);
                return next.map((seg, i) => ({
                  ...seg,
                  label: indexToLabel(i),
                }));
              });
            }
          },
        });
      }

      if (segmentIndex === deleteSegIndex) {
        updateSelection(null, null, null);
        removeSegmentDragControls();
      } else if (deleteSegIndex < segmentIndex) {
        updateSelection(segmentIndex - 1, null, null);
        setSegmentDragControls(segmentIndex - 1, setPolygonDetails);
      }
      setDeleteSegIndex(null);
    } else if (deletePanIndex != null) {
      const indices = Array.isArray(deletePanIndex)
        ? [...deletePanIndex].sort((a, b) => a - b)
        : [];
      const segment = POLYGONS[segmentIndex];
      const uiSegment = polygons?.[segmentIndex];
      const removedPanels = [];

      if (segment && indices.length) {
        indices.forEach((idx) => {
          const panelData = segment.panels[idx];
          const uiPanel = uiSegment?.panels?.[idx];
          if (panelData) {
            removedPanels.push({ index: idx, panelData, uiPanel: uiPanel ? { ...uiPanel } : null });
            if (panelData.panel) {
              sceneManager.scene.remove(panelData.panel);
            }
          }
        });

        segment.panels = segment.panels.filter((_, idx) => !indices.includes(idx));
        updatePanelList(indices);
        toast.success(indices.length > 1 ? "Panels deleted" : "Panel deleted");

        pushUndoAction({
          label: indices.length > 1 ? "Delete panels" : "Delete panel",
          undo: () => {
            const seg = POLYGONS[segmentIndex];
            if (!seg) return;
            const panels = [...seg.panels];
            removedPanels
              .sort((a, b) => a.index - b.index)
              .forEach((item) => {
                panels.splice(item.index, 0, item.panelData);
                if (item.panelData?.panel) sceneManager.scene.add(item.panelData.panel);
              });
            seg.panels = panels;

            if (typeof setPolygons === "function") {
              setPolygons((prev) => {
                if (!prev || !prev[segmentIndex]) return prev;
                const next = [...prev];
                const segUI = { ...next[segmentIndex] };
                const uiPanels = Array.isArray(segUI.panels) ? [...segUI.panels] : [];
                removedPanels
                  .sort((a, b) => a.index - b.index)
                  .forEach((item) => {
                    if (item.uiPanel) uiPanels.splice(item.index, 0, item.uiPanel);
                  });
                segUI.panels = uiPanels;
                next[segmentIndex] = segUI;
                return next;
              });
            }

            checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
          },
          redo: () => {
            const seg = POLYGONS[segmentIndex];
            if (!seg) return;
            removedPanels.forEach((item) => {
              if (item.panelData?.panel) sceneManager.scene.remove(item.panelData.panel);
            });
            seg.panels = seg.panels.filter(
              (p) => !removedPanels.some((r) => r.panelData?.panel === p?.panel)
            );
            if (typeof setPolygons === "function") {
              setPolygons((prev) => {
                if (!prev || !prev[segmentIndex]) return prev;
                const next = [...prev];
                const segUI = { ...next[segmentIndex] };
                const uiPanels = Array.isArray(segUI.panels) ? [...segUI.panels] : [];
                const removeIds = removedPanels
                  .map((r) => r.uiPanel?.panel_id)
                  .filter((id) => id != null);
                segUI.panels = uiPanels.filter((p) => !removeIds.includes(p.panel_id));
                next[segmentIndex] = segUI;
                return next;
              });
            }
            checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
          },
        });
      }
      updateSelection(segmentIndex, obsIndex, null);
      setDeletePanIndex(null);
    }

    setShowPanelSettings(false);
    updateDrawingMode(DrawingModes.NONE);
    setDeleteConfirmationVisible(false);
  };

  function updateSelection(segmentIndex, obstacleIndex, panelIndex) {
    setSelectedIndices({
      segmentIndex,
      obstacleIndex,
      panelIndex,
    });
  }

  useEffect(() => {
    activeRightPanelRef.current = activeRightPanel;
    if (typeof window !== "undefined") {
      window.isTreeUiActive = activeRightPanel === "trees";
    }
  }, [activeRightPanel]);

  useEffect(() => {
    const onUndoStackChanged = (event) => {
      const nextCanUndo = event?.detail?.canUndo;
      setHasUndo(typeof nextCanUndo === "boolean" ? nextCanUndo : canUndo());
    };

    refreshUndoState();
    window.addEventListener("UNDO_STACK_CHANGED", onUndoStackChanged);
    return () => window.removeEventListener("UNDO_STACK_CHANGED", onUndoStackChanged);
  }, []);

  const clearTreeSelection = useCallback(() => {
    if (!selectedTree) return;

    selectedTree.traverse((obj) => {
      if (obj.material?.emissive) {
        obj.material.emissive.set(0x000000);
      }
    });

    if (selectedTree.userData?.arrow) {
      selectedTree.userData.arrow.visible = false;
    }

    if (
      typeof window !== "undefined" &&
      window.transformControls?.object === selectedTree
    ) {
      try {
        window.transformControls.detach();
      } catch (e) {}
    }

    VARS.selectedTree = null;
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("TREE_SELECTED", { detail: { tree: null } })
      );
    }
  }, [selectedTree]);

  const handleRightPanelChange = useCallback(
    (panel) => {
      setActiveRightPanel(panel);
      if (panel === "segments") {
        clearTreeSelection();
      }
    },
    [clearTreeSelection]
  );

  useEffect(() => {
    removeExtraPanels(polygons);
    removeGrid();
  }, [currentDrawingModeRef.current, polygons]);

  useEffect(() => {
    disableUndoRecording();
    clearUndoStack();
    setHasUndo(false);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle(
      DRAWING_CURSOR_CLASS,
      DRAWING_CURSOR_MODES.has(currentDrawingModeRef.current)
    );
    return () => {
      document.body.classList.remove(DRAWING_CURSOR_CLASS);
      document.body.classList.remove("tree-drawing-plus-cursor");
    };
  }, []);

  useEffect(() => {
    if (informationChecked === "found" && modelProgress >= 98) {
      clearUndoStack();
      setHasUndo(false);
      enableUndoRecording();
    }
  }, [informationChecked, modelProgress]);

  // ✅ NEW: listen for tree selection/unselection from enableTreeDrawing.js
  useEffect(() => {
    const onTreeSelected = (e) => {
      const tree = e?.detail?.tree || null;

      if (!tree) {
        setSelectedTree(null);
        setCurrentSegment((prev) => (prev === 3 ? 0 : prev));
        return;
      }

      if (activeRightPanelRef.current !== "trees") {
        setActiveRightPanel("trees");
      }

      setSelectedTree(tree);

      // if tree selected -> open Tree Editor segment (index 3)
      setCurrentSegment((prev) => {
        if (tree) return 3;
        if (prev === 3) return 0;
        return prev;
      });
    };

    window.addEventListener("TREE_SELECTED", onTreeSelected);
    return () => window.removeEventListener("TREE_SELECTED", onTreeSelected);
  }, []);

  useEffect(() => {
    const onTrimSphereSelected = (e) => {
      const sphere = e?.detail?.sphere || null;
      if (!sphere) return;
      setSelectedTrimSphere(sphere);
      if (activeRightPanelRef.current !== "trees") {
        setActiveRightPanel("trees");
      }
    };

    window.addEventListener("TRIM_SPHERE_SELECTED", onTrimSphereSelected);
    return () => window.removeEventListener("TRIM_SPHERE_SELECTED", onTrimSphereSelected);
  }, []);

  useEffect(() => {
    const prev = prevSelectedIndicesRef.current || {};
    const curr = selectedIndices || {};

    const prevPanels = Array.isArray(prev.panelIndex) ? prev.panelIndex : [];
    const currPanels = Array.isArray(curr.panelIndex) ? curr.panelIndex : [];
    const panelChanged =
      prevPanels.length !== currPanels.length ||
      prevPanels.some((idx, i) => idx !== currPanels[i]);

    const selectionChanged =
      prev.segmentIndex !== curr.segmentIndex ||
      prev.obstacleIndex !== curr.obstacleIndex ||
      panelChanged;

    if (!selectionChanged) return;

    const prevSegmentIndex =
      prev.segmentIndex !== null && prev.segmentIndex !== undefined
        ? prev.segmentIndex
        : null;
    const currSegmentIndex =
      curr.segmentIndex !== null && curr.segmentIndex !== undefined
        ? curr.segmentIndex
        : null;

    if (prevSegmentIndex !== null && prevSegmentIndex !== currSegmentIndex) {
      syncPanelSelectionVisuals(prevSegmentIndex, []);
    }
    if (currSegmentIndex !== null) {
      syncPanelSelectionVisuals(currSegmentIndex, currPanels);
    }

    prevSelectedIndicesRef.current = {
      segmentIndex: curr.segmentIndex ?? null,
      obstacleIndex: curr.obstacleIndex ?? null,
      panelIndex: Array.isArray(curr.panelIndex) ? [...curr.panelIndex] : null,
    };

    const hasSegmentContextSelection =
      curr.segmentIndex !== null ||
      curr.obstacleIndex !== null ||
      currPanels.length > 0;

    if (!hasSegmentContextSelection) return;

    if (activeRightPanelRef.current !== "segments") {
      clearTreeSelection();
      setActiveRightPanel("segments");
    }
  }, [selectedIndices, clearTreeSelection]);

  useEffect(() => {
    const renderer = sceneManager?.renderer;
    const camera = sceneManager?.camera;
    if (!renderer || !camera) return;
    if (window.__treeSelectionListenerBound) return;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const onPointerDown = (event) => {
      const trees = TREES?.trees || [];
      if (!trees.length) return;
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);

      const hits = raycaster.intersectObjects(trees, true);
      if (!hits.length) return;
      let root = hits[0].object;
      while (root && !trees.includes(root)) {
        root = root.parent;
      }
      if (!root) return;
      window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree: root } }));
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.__treeSelectionListenerBound = true;
    return () => {
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      window.__treeSelectionListenerBound = false;
    };
  }, []);

  useEffect(() => {
    if (DEV_MOD) {
      setInformationChecked("found");
      setPanelList([
        {
          id: 0,
          image: "/assets/panels/panel14.jpg",
          name: "LG335",
          manufacturer: "LG",
          model: "LG335N1C-V5",
          notes: "",
          power_tolerance: 3,
          vmpp: 34.1,
          impp: 9.83,
          pmax: 335,
          voc: 9.83,
          isc: 41,
          max_series_fuse: 20,
          temp_pmax: -0.36,
          temp_voc: -0.27,
          temp_isc: 0.03,
          type: "Mono-crystalline",
          cell_quantity: "60",
          weight: "37.69",
          max_load_wind: "113",
          max_load_snow: "40",
          unit: "Metric",
          height: 1686,
          width: 1016,
          height_unit: "mm",
          width_unit: "mm",
        },
      ]);
    } else if (token && designID) {
      bootstrap();
      loadPanelList();
    } else {
      setInformationChecked("unauthenticated");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (panelList) {
      for (let panel of panelList) {
        let url = panel.image;
        getPanelImages(url)
          .then((res) => {
            if (res.status === 200) {
              const blob = res.data;
              const reader = new FileReader();
              reader.onload = () => {
                setPanelImage(panel.id, reader.result);
              };
              reader.readAsDataURL(blob);
            }
          })
          .catch((err) => console.error("Error fetching image:", err));
      }
    }
  }, [panelList]);

  
  const bootstrap = async () => {
    const [userResult, designResult] = await Promise.allSettled([
      checkUser(token),
      getDesign(token, designID),
    ]);

    
    if (userResult.status !== "fulfilled" || userResult.value?.status !== 200) {
      setInformationChecked("unauthenticated");
      return;
    }

    const userData = userResult.value?.data?.data || userResult.value?.data || {};
    const designerName =
      userData?.full_name ||
      userData?.name ||
      [userData?.first_name, userData?.last_name].filter(Boolean).join(" ");

    if (designerName) {
      try {
        window.localStorage.setItem("designerName", designerName);
      } catch (error) {}
    }

    persistReportBranding(userData);

    setCurrentUser(userData);

    if (designResult.status !== "fulfilled" || designResult.value?.status !== 200) {
      setCurrentDesign(null);
      setInformationChecked("not-found");
      return;
    }

    setCurrentDesign(designResult.value.data.data);
    setInformationChecked("found");
  };

  const checkModelInfo = async () => {
    getDesign(token, designID)
      .then(async (res) => {
        if (res.status === 200) {
          setCurrentDesign(res.data.data);
          setInformationChecked("found");
        }
      })
      .catch(() => {
        setCurrentDesign(null);
        setInformationChecked("not-found");
      });
  };

  const loadPanelList = () => {
    getPanelList(token)
      .then((res) => {
        if (res.status === 200) {
          setPanelList(res.data.data);
        }
      })
      .catch((err) =>
        notifyApiError(err, {
          fallback: "Couldn't load the solar panel catalog. Some panel options may be unavailable.",
          key: "panel-list",
          action: { label: "Retry", onClick: loadPanelList },
        })
      );
  };

  const handleUpdatePolygon = async () => {
    const data = transformPolygonData(polygons);
    const polygonData = JSON.stringify(data);
    setIsLoading(true);

    try {
      const res = await updateProjectData(token, currentDesign.uuid, {
        model_data: polygonData,
      });
      if (res?.status === 200) {
        message.success("Design saved successfully.");
      }
      return res;
    } catch (error) {
      handleErrors(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateDxf = async (options = {}) => {
    const { shouldDownload = true } = options;
    const data = transformPolygonData(polygons);
    const dxfPolygons = data?.polygons;

    if (!Array.isArray(dxfPolygons)) {
      console.error("Expected data.polygons to be an array but got:", data);
      return;
    }

    const result = generateDxf(dxfPolygons, currentDesign?.name || "design", {
      shouldDownload,
    });
    await uploadDesignerReport({
      token: token || "",
      projectId:
        designID ||
        currentDesign?.uuid ||
        currentDesign?.id ||
        currentDesign?.project_id ||
        "",
      reportType: "dxf",
      fileBlob: result?.blob,
      fileName: result?.fileName,
    });
  };

  const [currentSegment, setCurrentSegment] = useState(0);
  const [shadingValues, setShadingValues] = useState([]);
  const [removedSections, setRemovedSections] = useState({
    spheres: [],
    trimmedSections: [],
  });

  const handleUndoClick = () => {
    if (!canUndo()) {
      setHasUndo(false);
      return;
    }
    UndoLastAction();
    refreshUndoState();
  };

  return (
    <>
      {informationChecked == null ? (
        <LoadingScreen informationChecked={informationChecked} />
      ) : informationChecked === "unauthenticated" ? (
        <NotFound type={"token"} />
      ) : informationChecked === "not-found" ? (
        <NotFound type={"design"} />
      ) : (
        modelProgress < 98 && <LoadingScreen informationChecked={informationChecked} />
      )}

      {informationChecked === "found" && (
        <DesignerLayout
          currentProject={currentDesign}
          type={modelProgress < 98 ? "hidden" : "visible"}
          handleUpdatePolygon={handleUpdatePolygon}
          handleGenerateDxf={handleGenerateDxf}
          updateDrawingMode={updateDrawingMode}
          currentDrawingMode={currentDrawingMode}
          arePanelsOK={arePanelsOK}
          setCurrentSegment={setCurrentSegment}
          currentSegment={currentSegment}
          currentDesign={currentDesign}
          setShadingValues={setShadingValues}
          shadingValues={shadingValues}
          setRemovedSections={setRemovedSections}
          onUndo={handleUndoClick}
          canUndo={hasUndo}
          panelList={panelList}
          onDesignRefresh={checkModelInfo}
          rightPanelToggle={
            <RightPanelToggle
              activePanel={activeRightPanel}
              onChange={handleRightPanelChange}
            />
          }
          rightPanel={
            <Sections
              updateObstacleType={updateObstacleType}
              updateDrawingMode={updateDrawingMode}
              currentDrawingMode={currentDrawingMode}
              setDeleteConfirmationVisible={setDeleteConfirmationVisible}
              panelList={panelList}
              currentSegment={currentSegment}
              shadingValues={shadingValues}
              currentDesign={currentDesign}
              removedSections={removedSections}
              setRemovedSections={setRemovedSections}
              activePanel={activeRightPanel}
              selectedTrimSphere={selectedTrimSphere}
              // ✅ NEW
              selectedTree={selectedTree}
            />
          }
        >
          <Main
            setModelProgress={setModelProgress}
            currentDesign={currentDesign}
            currentDrawingModeRef={currentDrawingModeRef}
            obstacleTypeRef={obstacleTypeRef}
            updateDrawingMode={updateDrawingMode}
            setDeleteConfirmationVisible={setDeleteConfirmationVisible}
            panelList={panelList}
            arePanelsOK={arePanelsOK}
            setPanelsOK={setPanelsOK}
            setRemovedSections={setRemovedSections}
          />

          <ObstacleCopyPasteHotkeys setPolygonDetails={setPolygonDetails} />

          {deleteConfirmationVisible && (
            <DeleteConfirmation
              onConfirm={handleDeleteConfirmation}
              onCancel={handleDeleteCancellation}
            />
          )}

          {rulerDeleteTarget && (
            <DeleteConfirmation
              title="Delete Measurement?"
              description="This measurement will be deleted. This action cannot be undone."
              onConfirm={handleRulerDeleteConfirmation}
              onCancel={() => setRulerDeleteTarget(null)}
            />
          )}
        </DesignerLayout>
      )}
    </>
  );
};

export default Designer;
